import { RULE_TYPES } from "../../lib/rules.js";
import { I } from "../Icons.jsx";
import Btn from "../ui/Btn.jsx";
import Display from "../ui/Display.jsx";
import Eyebrow from "../ui/Eyebrow.jsx";
import IconBtn from "../ui/IconBtn.jsx";

export default function RebalanceOverlay({ proposals, rules, people, activities, busy, onAccept, onReject, onClose }) {
  const nameOf = (pid) => people.find(p => p.id === pid)?.Name || "?";
  const groupName = (actId, gid) => activities.find(a => a.id === actId)?.groups.find(g => g.id === gid)?.name || "?";
  const activityName = (aid) => activities.find(a => a.id === aid)?.name || "?";
  const ruleDesc = (rid) => {
    const r = rules.find(x => x.id === rid);
    if (!r) return "";
    const rt = RULE_TYPES.find(t => t.value === r.type);
    return `${nameOf(r.person_a)} ${rt?.label}${rt?.needsCount ? ` (${r.count})` : ""} ${nameOf(r.person_b)}`;
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(34,28,20,0.4)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--paper-0)", borderRadius: 14, padding: 28,
        width: "92%", maxWidth: 640, maxHeight: "86vh", overflow: "auto",
        border: "1px solid var(--line)", boxShadow: "0 24px 64px rgba(34,28,20,0.25)",
        color: "var(--ink)",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <Eyebrow>Suggested adjustments</Eyebrow>
            <Display size={24} style={{ marginTop: 8 }}>A gentle rebalance</Display>
            <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 6 }}>
              Each swap was chosen to reduce overall rule strain without introducing new breaks. Some resolve a rule outright; others chip away at rules like "at least N times" one activity at a time — accept, reopen, and accept again to keep going.
            </div>
          </div>
          <IconBtn icon={I.x} title="Close" onClick={onClose} />
        </div>

        {proposals.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic", padding: "8px 0" }}>
            No proposals — either no rules are broken, or no single swap can improve things further. Try editing seating manually or relaxing a rule.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {proposals.map((p, i) => {
              const resolves = p.afterBroken < p.beforeBroken;
              const label = resolves ? "resolves this rule" : "makes progress";
              return (
                <div key={p.id} style={{
                  padding: 16, borderRadius: 10,
                  border: "1px solid var(--line-2)", background: "var(--paper-1)",
                }}>
                  <Eyebrow style={{ marginBottom: 8 }}>
                    Swap {i + 1} · {label}
                  </Eyebrow>
                  <div style={{ fontSize: 14, color: "var(--ink)", marginBottom: 8, lineHeight: 1.55 }}>
                    In <strong>{activityName(p.swap.activityId)}</strong>: swap{" "}
                    <strong>{nameOf(p.swap.aPersonId)}</strong>
                    <span style={{ color: "var(--ink-3)" }}> ({groupName(p.swap.activityId, p.swap.aGroupId)})</span>
                    {" ⇄ "}
                    <strong>{nameOf(p.swap.bPersonId)}</strong>
                    <span style={{ color: "var(--ink-3)" }}> ({groupName(p.swap.activityId, p.swap.bGroupId)})</span>.
                  </div>
                  <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 12 }}>
                    {resolves ? "Resolves" : "Helps"}: {ruleDesc(p.ruleId)}
                    {" · "}
                    {resolves
                      ? `broken rules ${p.beforeBroken} → ${p.afterBroken}`
                      : `one step closer (open again for the next swap)`}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Btn variant="primary" size="sm" icon={I.check} disabled={busy} onClick={() => onAccept(i)}>
                      Accept swap
                    </Btn>
                    <Btn variant="quiet" size="sm" disabled={busy} onClick={() => onReject(i)}>
                      Dismiss
                    </Btn>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
